import { createReducer, on } from '@ngrx/store';

import { GlobalBookingTravelClass } from '@fcom/core';
import { TripType } from '@fcom/core/interfaces';
import {
  DestinationSearchFilters,
  DestinationSearchItem,
  DestinationSearchState,
} from '@fcom/destination-search/interfaces';

import { DestinationSearchFilterActions } from '../actions';

export const defaultFilters: DestinationSearchFilters = {
  selectedDestinations: [],
  tripDates: {
    departureDate: undefined,
    returnDate: undefined,
  },
  travelClass: GlobalBookingTravelClass.ECONOMY,
  tripType: TripType.RETURN,
};

const initialStates: DestinationSearchState = {
  default: defaultFilters,
};

const getUniqueDestinations = (destinations: DestinationSearchItem[]): DestinationSearchItem[] => {
  return destinations?.reduce((all: DestinationSearchItem[], current) => {
    const isUnique = all.every((d) => d.id !== current.id);
    return isUnique ? [...all, current] : all;
  }, []);
};

export const destinationSearchFilterReducer = createReducer(
  initialStates,
  on(DestinationSearchFilterActions.setDestinationSearchSelection, (state, action) => {
    return {
      ...state,
      [action.meta?.id]: {
        ...state[action.meta?.id],
        ...action.selection,
      },
    };
  }),
  on(DestinationSearchFilterActions.removeRouteUpdate, (state, action) => {
    return {
      ...state,
      [action.meta?.id]: {
        ...state[action.meta?.id],
        skipRouteUpdate: false,
      },
    };
  }),
  on(DestinationSearchFilterActions.setTripDates, (state, action) => {
    return {
      ...state,
      [action.meta?.id]: {
        ...state[action.meta?.id],
        tripDates: action.tripDates,
      },
    };
  }),
  on(DestinationSearchFilterActions.setTripType, (state, action) => {
    return {
      ...state,
      [action.meta?.id]: {
        ...state[action.meta?.id],
        tripType: action.tripType,
      },
    };
  }),
  on(DestinationSearchFilterActions.setTravelClass, (state, action) => {
    return {
      ...state,
      [action.meta?.id]: {
        ...state[action.meta?.id],
        travelClass: action.travelClass,
      },
    };
  }),
  on(DestinationSearchFilterActions.setFromLocationCode, (state, action) => {
    return {
      ...state,
      [action.meta?.id]: {
        ...state[action.meta?.id],
        fromLocationCode: action.fromLocationCode,
      },
    };
  }),
  on(DestinationSearchFilterActions.setSelectedDestinations, (state, action) => {
    return {
      ...state,
      [action.meta?.id]: {
        ...state[action.meta?.id],
        selectedDestinations: getUniqueDestinations(action.selectedDestinations),
      },
    };
  }),
  on(DestinationSearchFilterActions.setRestrictToDestinations, (state, action) => {
    return {
      ...state,
      [action.meta?.id]: {
        ...state[action.meta?.id],
        restrictToDestinations: action.restrictToDestinations,
      },
    };
  }),
  on(DestinationSearchFilterActions.setMaxPrice, (state, { meta, maxPrice }) => {
    return {
      ...state,
      [meta?.id]: {
        ...state[meta?.id],
        maxPrice,
      },
    };
  }),
  on(DestinationSearchFilterActions.setShowDirectFlightsOnly, (state, { meta, showDirectFlightsOnly }) => {
    return {
      ...state,
      [meta?.id]: {
        ...state[meta?.id],
        showDirectFlightsOnly,
      },
    };
  }),
  on(DestinationSearchFilterActions.setDestinationsBeforeFiltering, (state, { meta, destinationsBeforeFiltering }) => {
    return {
      ...state,
      [meta?.id]: {
        ...state[meta?.id],
        destinationsBeforeFiltering,
      },
    };
  }),
  on(DestinationSearchFilterActions.setRoundTripPrices, (state, { meta, roundTripPrices }) => {
    return {
      ...state,
      [meta?.id]: {
        ...state[meta?.id],
        roundTripPrices,
      },
    };
  })
);
